import type { ReactNode } from "react";
import { space, type SpaceToken } from "./tokens.ts";
import "./columns.css";

export type ColumnsProps = {
  /** The wide column (a preview, a document). */
  main: ReactNode;
  /** The narrow column beside it (details, a panel), `sideWidth` wide. */
  side: ReactNode;
  /**
   * Below this width of the CONTAINER (not the window: a pane in a split can be narrow on a wide screen) the columns
   * stack, main on top, both full width. Default 720.
   */
  stackBelow?: 600 | 720 | 900;
  /** The side column's width when side by side: "dialog" (≈ a medium sheet, 380) or "panel" (320). */
  sideWidth?: "dialog" | "panel";
  gap?: SpaceToken;
  testID?: string;
};

/**
 * Two columns that never overlap: side by side when the container is wide enough, stacked (main first) below
 * `stackBelow`. Web: CSS grid + a container query. Native: `ViewThatFits` / a width-class switch (iOS),
 * `BoxWithConstraints` (Android).
 */
export function Columns({ main, side, stackBelow = 720, sideWidth = "dialog", gap = "6", testID }: ColumnsProps) {
  return (
    <div className="b-columns-host" data-testid={testID}>
      <div className={`b-columns b-columns-${stackBelow} b-columns-side-${sideWidth}`} style={{ gap: space(gap) }}>
        <div className="b-columns-main">{main}</div>
        <div className="b-columns-side">{side}</div>
      </div>
    </div>
  );
}
